import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetAllUsers } from 'domains/admin/hooks/useGetAllUsers';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';
import { useTranslation } from 'i18n/TranslationProvider';
import AllUsersItem from './AllUsersItem';
import {
  UnifiedEmptyState,
  UnifiedFilterBar,
  UnifiedLoadingState,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';

const normalize = (value) => String(value || '').toLowerCase().trim();

const AllUsersList = () => {
  const { t } = useTranslation();
  const { users, loading, error, refetch } = useGetAllUsers();
  const { tableSx } = useUnifiedTableStyles();
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const panelBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(13,23,53,0.72)');

  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = normalize(searchParams.get('role') || 'all');
  const statusFilter = normalize(searchParams.get('status') || 'all');
  const uidFilter = normalize(searchParams.get('uid') || '');
  const query = searchParams.get('q') || '';
  const page = Math.max(Number.parseInt(searchParams.get('page') || '1', 10), 1);
  const limit = Math.max(Number.parseInt(searchParams.get('limit') || '25', 10), 10);

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  };

  const filteredUsers = useMemo(() => {
    const source = Array.isArray(users) ? users : [];
    return source.filter((user) => {
      if (roleFilter !== 'all' && normalize(user.role) !== roleFilter) return false;
      if (statusFilter !== 'all' && normalize(user.validationStatus) !== statusFilter) return false;
      if (uidFilter && normalize(user.firebaseUid) !== uidFilter) return false;

      if (!query.trim()) return true;
      const haystack = [
        user.displayName,
        user.email,
        user.companyName,
        user.firebaseUid,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query.trim().toLowerCase());
    });
  }, [users, roleFilter, statusFilter, uidFilter, query]);

  const total = filteredUsers.length;
  const totalPages = Math.ceil(total / limit) || 0;
  const currentPage = Math.min(page, totalPages || 1);
  const startIndex = (currentPage - 1) * limit;
  const pagedUsers = filteredUsers.slice(startIndex, startIndex + limit);
  const visibleFrom = total === 0 ? 0 : startIndex + 1;
  const visibleTo = total === 0 ? 0 : Math.min(startIndex + limit, total);

  if (loading) {
    return (
      <UnifiedSurfaceCard p={8}>
        <UnifiedLoadingState label={t('Loading users...')} minH="120px" />
      </UnifiedSurfaceCard>
    );
  }

  if (error) {
    return (
      <UnifiedSurfaceCard p={5}>
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          {t('Error fetching users')}: {error}
        </Alert>
      </UnifiedSurfaceCard>
    );
  }

  return (
    <Stack spacing={4}>
      <UnifiedSurfaceCard
        p={5}
        bg={panelBg}
      >
        <Stack spacing={4}>
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
            <Text fontWeight="700">{t('All Users')}</Text>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              {t('Refresh')}
            </Button>
          </Flex>

          <UnifiedFilterBar>
            <Box minW={{ base: '100%', md: '220px' }}>
              <Input
                value={query}
                placeholder={t('Search by name, email, company, uid')}
                onChange={(event) =>
                  setParam({ q: event.target.value, page: 1 })
                }
              />
            </Box>
            <Box minW={{ base: '100%', md: '180px' }}>
              <Select
                value={roleFilter}
                onChange={(event) =>
                  setParam({ role: event.target.value, page: 1 })
                }
              >
                <option value="all">{t('All roles')}</option>
                <option value="client">{t('Client')}</option>
                <option value="partner">{t('Partner')}</option>
                <option value="admin">{t('Admin')}</option>
              </Select>
            </Box>
            <Box minW={{ base: '100%', md: '180px' }}>
              <Select
                value={statusFilter}
                onChange={(event) =>
                  setParam({ status: event.target.value, page: 1 })
                }
              >
                <option value="all">{t('All statuses')}</option>
                <option value="pending">{t('Pending')}</option>
                <option value="approved">{t('Approved')}</option>
                <option value="rejected">{t('Rejected')}</option>
                <option value="disabled">{t('Disabled')}</option>
              </Select>
            </Box>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setParam({ q: '', role: 'all', status: 'all', uid: '', page: 1 })
                }
              >
                {t('Reset')}
              </Button>
            </HStack>
          </UnifiedFilterBar>
        </Stack>
      </UnifiedSurfaceCard>

      {total === 0 ? (
        <UnifiedSurfaceCard p={4}>
          <UnifiedEmptyState
            title={t('No users found for current filters.')}
            description={t('Try broadening the search, role, or status filters.')}
            minH="140px"
          />
        </UnifiedSurfaceCard>
      ) : (
        <UnifiedSurfaceCard p={4} overflow="hidden">
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={3} gap={2} flexWrap="wrap">
            <Text fontSize="sm" color={mutedText}>
              {t('Showing')} {visibleFrom}-{visibleTo} {t('of')} {total}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color={mutedText}>
                {t('Rows')}
              </Text>
              <Select
                size="sm"
                value={String(limit)}
                onChange={(event) => setParam({ limit: event.target.value, page: 1 })}
                w="92px"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </HStack>
          </Flex>

          <Box overflowX="auto">
            <Table size="sm" sx={{ minWidth: '1220px', ...tableSx }}>
              <Thead>
                <Tr>
                  <Th>{t('User')}</Th>
                  <Th>{t('Email')}</Th>
                  <Th>{t('Company')}</Th>
                  <Th>{t('Role')}</Th>
                  <Th>{t('Status')}</Th>
                  <Th>{t('Actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedUsers.map((user) => (
                  <AllUsersItem
                    key={user.firebaseUid || user.id}
                    user={user}
                    refetch={refetch}
                    tableMode
                  />
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex mt={4} justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
            <Text fontSize="sm" color={mutedText}>
              {t('Total')}: {total}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage - 1 })}
                isDisabled={currentPage <= 1}
              >
                {t('Previous')}
              </Button>
              <Text fontSize="sm" minW="100px" textAlign="center">
                {t('Page')} {currentPage}
                {totalPages ? ` / ${totalPages}` : ''}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage + 1 })}
                isDisabled={!totalPages || currentPage >= totalPages}
              >
                {t('Next')}
              </Button>
            </HStack>
          </Flex>
        </UnifiedSurfaceCard>
      )}
    </Stack>
  );
};

export default AllUsersList;
